import React from 'react';
import { Mutation } from 'react-apollo';
import { withRouter } from 'react-router';
import moment from 'moment';

import { isToggleActive } from '../../../components/featureToggle/toggles';
import { Text, Flex, Button, Box } from 'rebass';
import styled from 'styled-components';
import { loadStripe } from '@stripe/stripe-js';
import { Icon } from '@iconify/react';
import visaIcon from '@iconify/icons-logos/visa';
import mcIcon from '@iconify/icons-logos/mastercard';
import amexIcon from '@iconify/icons-logos/amex';
import discoverIcon from '@iconify/icons-logos/discover';
import creditCard from '@iconify/icons-entypo/credit-card';

import {
  DONT_CANCEL_MEMBERSHIP,
  GET_MEMBERSHIPS,
  MEMBERSHIP_UPDATE_PAYMENT_CREATE_SESSION,
  RESUME_SUBSCRIPTION,
  GET_MEMBERSHIP_HANDOFF_URL,
  CANCEL_SUBSCRIPTION,
  CURRENT_USER,
} from '../../../graphql';
import { isMobile } from 'react-device-detect';
import ConfirmModal from '../../../components/ConfirmModal';
import { useState } from 'react';

const ModalInner = styled(Box)`
  max-width: 600px;
  width: 100%;
`;
const ContentWrapper = styled(Flex)`
  flex: 1;
  flex-direction: column;
  background-color: white;
  margin: 24px 12px;
`;

const CardWrapperRow = styled(Flex)`
  flex: 1;
  flex-direction: row;
  margin-top: 24px;
  margin-bottom: 24px;
`;

const CardWrapperText = styled(Flex)`
  flex: 1;
  flex-direction: column;
`;

const Title = styled(Text)`
  font-weight: 600;
  font-size: 20px;
  margin-bottom: 24px;
`;

const PriceText = styled(Text)`
  font-weight: 600;
  color: #f6a293;
`;

const ActionButton = styled(Button)`
  height: 40px;
  padding: 0 24px;
  width: auto;
`;

const ActionsContainer = styled(Flex)`
  margin-top: 24px;
  margin-right: 16px;
`;

const A = styled.a`
  display: flex;
  text-decoration: none;
  color: #f6a293;

  &:visited {
    color: #f6a293;
  }
`;

const TableRow = styled(Flex)`
  flex: 1;
  flex-direction: row;
  max-width: 700px;
  background: ${(props) => (props.odd ? 'transparent' : '#F4F4F4')};
`;

const TableHeaderCell = styled(Text)`
  flex: 0.25;
  font-weight: 400;
`;

const TableHeaderCellRest = styled(Text)`
  flex: 1;
  font-weight: 400;
  @media screen and (max-width: 480px) {
    display: none;
  }
`;

const TableCell = styled(Text)`
  flex: 0.25;
  font-weight: 400;
`;

const TableCellRest = styled(Text)`
  flex: 1;
  font-weight: 400;
  @media screen and (max-width: 480px) {
    display: none;
  }
`;

function getFormattedDate(date) {
  var year = date.getFullYear();

  var month = (1 + date.getMonth()).toString();
  month = month.length > 1 ? month : '0' + month;

  var day = date.getDate().toString();
  day = day.length > 1 ? day : '0' + day;

  return month + '/' + day + '/' + year;
}

const SectionDetails = ({ data, type, history, isOnPausedMembership }) => {
  const [isWantCancel, setIsWantCancel] = useState(false);
  if (!data) return <div />;
  let memberPricing = `Your next bill is for $${(data.nextBill.amount || 0) / 100} on ${getFormattedDate(new Date(data.nextBill.date * 1000))}`;
  if (data) {
    if (data.hasCancellationCoupon) {
      memberPricing = `A ${data.hasCancellationCoupon.discountPercent}% discount will be applied to your next bill on ${getFormattedDate(new Date(data.nextBill.date * 1000))}`;
    }
  }

  let priceText = memberPricing;

  if (data.pausedSubscription && data.pausedSubscription.resumesAt) {
    priceText = `Your subscription is paused until ${moment.unix(data.pausedSubscription.resumesAt).format('MMMM Do YYYY')}`;
  }

  if (data.cancels && data.cancels.cancelsAt) {
    priceText = `Your ${type === 'membership' ? 'membership' : 'subscription'} will remain active until ${moment.unix(data.cancels.cancelsAt).format('MMMM Do YYYY')}`;
  }

  let logo = creditCard;
  if (data && data.card) {
    switch ((data.card.brand || '').toUpperCase()) {
      case 'VISA':
        logo = visaIcon;
        break;
      case 'MC':
      case 'MASTERCARD':
      case 'MASTER CARD':
        logo = mcIcon;
        break;
      case 'AX':
      case 'AMEX':
      case 'AMERICANEXPRESS':
      case 'AMERICAN EXPRESS':
        logo = amexIcon;
        break;
      case 'DISCO':
      case 'DISCOVER':
        logo = discoverIcon;
        break;

      default:
        logo = creditCard;
        break;
    }
  }

  return (
    <ContentWrapper>
      {!!isToggleActive('MEMBERSHIP_CANCEL_FLOW') &&
        type === 'membership' &&
        isOnPausedMembership && (
          <Flex mb={4} style={{ width: '100%', padding: '12px 16px', background: '#F4F4F4', borderRadius: '8px' }} flexDirection={'column'}>
            <Text fontWeight={500}>The Pause Plan keeps your account and select member benefits active while you take a break from your full membership.</Text>
            <Text mt={3} mb={2}>While you’re on the Pause Plan:</Text>
            <ul>
              <li>
                <Text>You'll be charged $8/month</Text>
              </li>
              <li>
                <Text>You can message your doctor</Text>
              </li>
              <li>
                <Text>Your records, labs, and care plan stay active</Text>
              </li>
              <li>
                <Text>You keep member pricing on kits and supplements</Text>
              </li>
            </ul>
            <Text mt={2}><span style={{fontWeight: 500}}>Provider visits are not included.</span> To schedule a visit, switch back to a full membership.</Text>
          </Flex>
        )}
      <Title>Payment</Title>
      <Text>{priceText}</Text>

      <CardWrapperRow>
        {data && data.card && data.card.last4 && <Icon icon={logo} style={{ marginRight: '8px' }} />}
        <CardWrapperText>
          {!!data && data.card &&(
          <Text>{`${data.card.brand} ending in ${data.card.last4}`}</Text>
          )}
          <Mutation
            mutation={MEMBERSHIP_UPDATE_PAYMENT_CREATE_SESSION}
            variables={{
              subscriptionId: data.id,
              customerId: data.customer,
              forwardUrl: 'subscription/update/success',
              forwardUrlCancel: 'my-subscriptions',
            }}
            onCompleted={async (res) => {
              const stripePromise = loadStripe(window._env_.REACT_APP_STRIPE_KEY);
              const stripe = await stripePromise;
              await stripe.redirectToCheckout({
                sessionId: res.createUpdatePaymentMethodSession.sessionId,
              });
            }}
          >
            {(createSession, { loading }) => (
              <PriceText
                onClick={() => {
                  if (loading) return;
                  createSession();
                }}
              >
                Update
              </PriceText>
            )}
          </Mutation>
        </CardWrapperText>
      </CardWrapperRow>

      <Title>Receipts</Title>

      <TableRow odd={true}>
        <TableHeaderCell
          style={{
            width: isMobile ? '90px' : '',
            minWidth: isMobile ? '90px' : '',
          }}
        >
          Date
        </TableHeaderCell>
        <TableHeaderCellRest>Reference</TableHeaderCellRest>
        <TableHeaderCell>Price</TableHeaderCell>
        <TableHeaderCell />
      </TableRow>
      {data.receipts.map((r, i) => {
        return (
          <TableRow key={r.id} odd={i % 2 !== 0}>
            <TableCell
              style={{
                width: isMobile ? '90px' : '',
                minWidth: isMobile ? '90px' : '',
              }}
            >
              {getFormattedDate(new Date(r.date * 1000))}
            </TableCell>
            <TableCellRest>{r.reference}</TableCellRest>
            <TableCell>${r.amount / 100}</TableCell>
            <TableCell>
              <A href={r.link} target="_blank">
                view
              </A>
            </TableCell>
          </TableRow>
        );
      })}
      {!!isToggleActive('MEMBERSHIP_CANCEL_FLOW') && type === 'membership' ? (
        <>
          {isOnPausedMembership ? (
            <Flex justifyContent={'center'} alignItems={'center'} flexDirection={['column-reverse', 'row']} mt={3} style={{ gap: '20px' }}>
              {/* <Mutation
                mutation={CANCEL_SUBSCRIPTION}
                refetchQueries={[{ query: GET_MEMBERSHIPS }, { query: CURRENT_USER }]}
                variables={{
                  isMembership: true,
                  subscriptionId: data.id,
                }}
              >
                {(cancelSubscription, { loading }) => ( */}
              <ActionButton style={{ maxWidth: 'fit-content', alignSelf: 'center' }} variant="outline" onClick={() => setIsWantCancel(true)}>
                Cancel entirely
              </ActionButton>
              {/* )} */}
              {/* </Mutation> */}

              <ActionButton style={{ maxWidth: 'fit-content', alignSelf: 'center' }} variant="primary" onClick={() => history.push('/become-member')}>
                Switch to full membership
              </ActionButton>
            </Flex>
          ) : (
            <Mutation
              mutation={GET_MEMBERSHIP_HANDOFF_URL}
              variables={{
                subscriptionId: data.id,
              }}
              onCompleted={(e) => {
                if (e.membershipHandoffUrl) {
                  window.location.href = e.membershipHandoffUrl;
                }
              }}
            >
              {(getHandoff, { loading }) => (
                <ActionButton style={{ maxWidth: 'fit-content', alignSelf: 'center' }} mt={3} disabled={!!loading} variant="outline" onClick={() => getHandoff()}>
                  Cancel
                </ActionButton>
              )}
            </Mutation>
          )}
          {/* membershipHandoffUrl */}
        </>
      ) : (
        <>
          {data.cancels &&
            data.cancels.cancelsAt && (
              <ActionsContainer>
                <Mutation
                  mutation={DONT_CANCEL_MEMBERSHIP}
                  variables={{
                    id: data.id,
                  }}
                  refetchQueries={[{ query: GET_MEMBERSHIPS }]}
                >
                  {(dontCancelSubscription, { loading }) => (
                    <ActionButton variant="outline" onClick={() => dontCancelSubscription()}>
                      Don't Cancel Membership
                    </ActionButton>
                  )}
                </Mutation>
              </ActionsContainer>
            )}
          {(!data.cancels || !data.cancels.cancelsAt) && (
            <>
              {type === 'membership' ? (
                <ActionsContainer>
                  <ActionButton
                    variant="outline"
                    onClick={() =>
                      history.push({
                        pathname: '/cancel-membership',
                        state: {
                          id: data.id,
                          interval: data.interval,
                        },
                      })
                    }
                  >
                    Cancel Membership
                  </ActionButton>
                </ActionsContainer>
              ) : (
                <Flex>
                  {data.pausedSubscription && data.pausedSubscription.resumesAt ? (
                    <ActionsContainer>
                      <Mutation
                        mutation={RESUME_SUBSCRIPTION}
                        refetchQueries={[{ query: GET_MEMBERSHIPS }]}
                        variables={{
                          subscriptionId: data.id,
                        }}
                      >
                        {(resumeSubscription, { loading }) => (
                          <ActionButton variant="outline" onClick={() => resumeSubscription()}>
                            Resume Subscription
                          </ActionButton>
                        )}
                      </Mutation>
                    </ActionsContainer>
                  ) : (
                    <ActionsContainer>
                      <ActionButton
                        variant="outline"
                        onClick={() =>
                          history.push({
                            pathname: '/pause-subscription',
                            state: {
                              id: data.id,
                              type: type,
                            },
                          })
                        }
                      >
                        Pause Subscription
                      </ActionButton>
                    </ActionsContainer>
                  )}
                  <ActionsContainer>
                    <ActionButton
                      variant="outline"
                      onClick={() =>
                        history.push({
                          pathname: '/cancel-subscription',
                          state: {
                            id: data.id,
                            type: type,
                            canPause: !data.pausedSubscription && !data.pausedSubscription.resumesAt,
                          },
                        })
                      }
                    >
                      Cancel Subscription
                    </ActionButton>
                  </ActionsContainer>
                </Flex>
              )}
            </>
          )}
        </>
      )}
      <ConfirmModal
        isOpen={isWantCancel}
        onClose={() => setIsWantCancel(false)}
        onBackgroundClick={() => setIsWantCancel(false)}
        onEscapeKeydown={() => setIsWantCancel(false)}
        confirmLabel="Yes, cancel"
        cancelLabel="No, keep it paused"
        confirmVariant="outline"
        cancelVariant="primary"
        buttonsDirection="column-reverse"
        childrenManaged
        displayX={true}
        cardStyle={{
          width: '500px',
          maxWidth: 'calc(100vw - 40px)',
        }}
      >
        <ModalInner>
          <Text fontSize={3} fontWeight={500} textAlign="center" mb={3}>
            Are you sure?
          </Text>
          <Text textAlign="center">If you cancel the paused membership:</Text>
          <Flex justifyContent={'center'} alignItems={'center'} mb={4} mt={2} style={{ width: '100%', padding: '12px 16px', background: '#F4F4F4', borderRadius: '8px' }}>
            <Flex>
              <ul>
                <li>
                  <Text>Lose access to your doctor</Text>
                </li>
                <li>
                  <Text>Your care plan and history are no longer accessible</Text>
                </li>
                <li>
                  <Text>Full price if you come back</Text>
                </li>
                <li>
                  <Text>You may need to re-onboard with a new provider</Text>
                </li>
              </ul>
            </Flex>
          </Flex>
          <Mutation
            mutation={CANCEL_SUBSCRIPTION}
            refetchQueries={[{ query: GET_MEMBERSHIPS }, { query: CURRENT_USER }]}
            awaitRefetchQueries
            variables={{
              isMembership: true,
              subscriptionId: data.id,
            }}
            onCompleted={() => setIsWantCancel(false)}
          >
            {(cancelSubscription, { loading }) => (
              <Flex alignItems={'center'} justifyContent={'center'}>
              <Flex flexDirection={'column'}>
                <Button style={{width:'100%'}} onClick={()=>setIsWantCancel(false)} disabled={!!loading} variant={'primary'}>
                  No, keep it paused
                </Button>
                <Button style={{width:'100%'}} mt={3} disabled={!!loading} variant={'outline'} onClick={() => cancelSubscription()}>
                  Yes, cancel it
                </Button>
              </Flex>
              </Flex>
            )}
          </Mutation>
        </ModalInner>
      </ConfirmModal>
    </ContentWrapper>
  );
};

export default withRouter(SectionDetails);
